import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * UnauthorizedPage Component
 *
 * Displays a user-friendly message when a user attempts to access a page
 * they don't have permission to view.
 *
 * Requirements:
 * - 14.2: Display user-friendly message for insufficient permissions
 * - 14.2: Provide link to return to authorized page
 */
export const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate();
  const { staffUser } = useAuth();

  const handleGoBack = () => {
    // Navigate back to the previous page or dashboard
    navigate(-1);
  };

  const getAuthorizedPage = () => {
    // Determine a safe page to redirect to based on user role
    if (!staffUser?.role) {
      return '/login';
    }

    // All roles have access to at least one of these pages
    switch (staffUser.role) {
      case 'dispatch':
        return '/live-orders';
      case 'accounts':
        return '/dashboard';
      case 'operations':
        return '/dashboard';
      case 'admin':
        return '/dashboard';
      case 'super_admin':
        return '/dashboard';
      default:
        return '/dashboard';
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full text-center">
        {/* Icon */}
        <div className="mb-6">
          <svg
            className="mx-auto h-24 w-24 text-red-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Access Denied</h1>

        {/* Message */}
        <p className="text-lg text-gray-600 mb-8">
          You don't have permission to access this page. Please contact your administrator if you
          believe this is an error.
        </p>

        {/* Role Information */}
        {staffUser && (
          <p className="text-sm text-gray-500 mb-8">
            Your current role:{' '}
            <span className="font-semibold capitalize">{staffUser.role.replace('_', ' ')}</span>
          </p>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleGoBack}
            className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 shadow-sm text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <svg className="mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Go Back
          </button>

          <Link
            to={getAuthorizedPage()}
            className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            Go to Dashboard
            <svg className="ml-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
        </div>
      </div>
    </div>
  );
};
